// src/pages/Admin/Techniques/EditTechniqueModal.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  FaSave,
  FaTimes,
  FaImage,
  FaBookOpen,
  FaPlus,
  FaUpload,
} from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";
import {
  updateTechnique,
  getAllTechniques,
} from "../../../services/techniquesServices";
import { getBeltsList } from "../../../services/beltServices";

// Services upload local (sharp) pour techniques
import {
  uploadTechniquePhoto,
  toAbsoluteTechniquePhotoUrl,
} from "../../../services/techniquesPhotoServices";

const DEFAULT_IMG = "/img/no-image.jpg";

// Helper cache-busting
const withBust = (url, v) =>
  url ? `${url}${url.includes("?") ? "&" : "?"}v=${v}` : url;

// Helpers ceintures (tri par id)
const beltId = (b) =>
  b?.id ??
  b?.belt_id ??
  b?.ordre ??
  b?.order ??
  b?.rank ??
  Number.MAX_SAFE_INTEGER;
const beltLabel = (b) =>
  (b &&
    (b.label ||
      b.nom ||
      b.name ||
      b.belt_name ||
      b.couleur ||
      b.color ||
      b.belt)) ||
  String(b || "");

export default function EditTechniqueModal({
  isOpen,
  onClose,
  refresh,
  technique, // objet technique à éditer
  existingFamilies, // array<string> — optionnel
}) {
  const [formData, setFormData] = useState({
    nom: "",
    famille: "",
    ceinture: "",
    image1_url: "",
    video_url: "",
    traduction: "",
    information: "",
    uv2: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [imgError, setImgError] = useState(false);

  // Aperçu + cache-busting
  const [imgVersion, setImgVersion] = useState(0);

  // Familles / Ceintures dynamiques
  const [families, setFamilies] = useState([]);
  const [belts, setBelts] = useState([]); // [{id,label}]

  // Ajout de famille (dans la modale)
  const [familyMode, setFamilyMode] = useState("existing"); // "existing" | "new"
  const [newFamily, setNewFamily] = useState("");

  // Sous-modale upload
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [outputFormat, setOutputFormat] = useState("webp"); // "webp" | "jpg" | "png"

  // Reset + pré-remplissage
  useEffect(() => {
    if (!isOpen || !technique) return;
    setFormData({
      nom: technique.nom || "",
      famille: technique.famille || "",
      ceinture: technique.ceinture || "",
      image1_url: technique.image1_url || "",
      video_url: technique.video_url || "",
      traduction: technique.traduction || "",
      information: technique.information || "",
      uv2: technique.uv2 || "",
    });
    setImgError(false);
    setSubmitting(false);
    setFamilyMode("existing");
    setNewFamily("");
    setIsUploadOpen(false);
    setSelectedFile(null);
    setUploading(false);
    setUploadProgress(0);
    setImgVersion((v) => v + 1);
  }, [isOpen, technique]);

  // Charger familles
  const loadFamilies = useCallback(async () => {
    try {
      if (Array.isArray(existingFamilies) && existingFamilies.length > 0) {
        const sorted = [
          ...new Set(existingFamilies.map((f) => (f || "").trim())),
        ]
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
        const withCurrent =
          formData.famille && !sorted.includes(formData.famille)
            ? [...sorted, formData.famille].sort((a, b) => a.localeCompare(b))
            : sorted;
        setFamilies(withCurrent);
        return;
      }
      const data = await getAllTechniques();
      const setF = new Set();
      (Array.isArray(data) ? data : []).forEach((t) => {
        const f = (t?.famille || "").trim();
        if (f) setF.add(f);
      });
      if (formData.famille) setF.add(formData.famille);
      setFamilies(Array.from(setF).sort((a, b) => a.localeCompare(b)));
    } catch (e) {
      console.error("Erreur chargement familles:", e);
      setFamilies(formData.famille ? [formData.famille] : []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingFamilies, formData.famille]);

  // Charger ceintures
  const loadBelts = useCallback(async () => {
    try {
      const res = await getBeltsList();
      const items = Array.isArray(res)
        ? res
            .map((b) => ({ id: beltId(b), label: (beltLabel(b) || "").trim() }))
            .filter((b) => b.label)
            .sort((a, b) => Number(a.id) - Number(b.id))
        : [];
      setBelts(items);
    } catch (e) {
      console.error("Erreur chargement ceintures:", e);
      setBelts([]);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    loadFamilies();
    loadBelts();
  }, [isOpen, loadFamilies, loadBelts]);

  if (!isOpen || !technique) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (name === "image1_url") {
      setImgError(false);
      setImgVersion((v) => v + 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nom.trim()) {
      toast.error("Le nom est requis.");
      return;
    }

    setSubmitting(true);
    try {
      const chosenFamily =
        familyMode === "new"
          ? newFamily.trim()
          : (formData.famille || "").trim();

      const payload = {
        nom: formData.nom.trim(),
        famille: chosenFamily || null,
        ceinture: formData.ceinture?.trim() || null,
        image1_url:
          formData.image1_url?.trim() && !imgError
            ? formData.image1_url.trim()
            : DEFAULT_IMG,
        video_url: formData.video_url?.trim() || null,
        traduction: formData.traduction?.trim() || null,
        information: formData.information?.trim() || null,
        uv2: formData.uv2?.trim() || null,
      };

      await updateTechnique(technique.id, payload);
      toast.success("Technique mise à jour avec succès");
      refresh?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur mise à jour technique:", error);
      toast.error("Erreur lors de la mise à jour de la technique");
    } finally {
      setSubmitting(false);
    }
  };

  // Upload image (local sharp) — sous-modale
  const onFileChange = (e) => setSelectedFile(e.target.files?.[0] || null);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!technique?.id) {
      toast.error("Technique introuvable — impossible d’associer l’image.");
      return;
    }
    if (!selectedFile) {
      toast.warn("Sélectionnez un fichier image.");
      return;
    }

    const okType = /^image\/(png|jpe?g|webp)$/i.test(selectedFile.type);
    if (!okType) {
      toast.error("Format d'image non supporté. Utilisez PNG / JPG / WEBP.");
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo).");
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const updated = await uploadTechniquePhoto(technique.id, selectedFile, {
        format: outputFormat,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const p = Math.round((evt.loaded / evt.total) * 100);
          setUploadProgress(p);
        },
      });

      setFormData((prev) => ({
        ...prev,
        image1_url: updated?.image1_url || prev.image1_url,
      }));
      setImgVersion((v) => v + 1);

      toast.success("Image téléversée et optimisée.");
      setIsUploadOpen(false);
      setSelectedFile(null);
    } catch (err) {
      console.error("Upload image technique:", err);
      toast.error("Erreur lors du téléversement de l’image.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Prépare l’aperçu (utilise toAbsolute* pour /uploads/…)
  const previewAbs = toAbsoluteTechniquePhotoUrl(
    imgError || !formData.image1_url?.trim() ? DEFAULT_IMG : formData.image1_url
  );
  const imgSrc = withBust(previewAbs, imgVersion);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Modal wrapper (scrollable) */}
      <div className="relative w-full max-w-4xl overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70 max-h-[85vh] flex flex-col">
        {/* Header dégradé */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <div className="flex items-center justify-center gap-2 text-white">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/20">
              <FaBookOpen />
            </span>
            <h3 className="text-lg sm:text-xl font-bold uppercase">
              Modifier une technique
            </h3>
          </div>
          <p className="text-white/90 text-center text-xs mt-1">
            Mettez à jour les informations ci-dessous.
          </p>
        </div>

        {/* Body scrollable */}
        <form onSubmit={handleSubmit} className="p-5 sm:p-6 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
            {/* Colonne image / aperçu */}
            <div className="space-y-3">
              <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-4 flex items-center justify-center min-h-[260px] relative">
                <img
                  src={imgSrc}
                  alt="Aperçu technique"
                  onError={() => setImgError(true)}
                  className="max-h-[220px] max-w-full object-contain rounded-lg shadow-sm"
                />
                <span className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full grid place-items-center bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 shadow ring-2 ring-white dark:ring-slate-900">
                  <FaImage className="text-xs" />
                </span>
              </div>

              <Button
                type="button"
                text="Téléverser une image"
                icon={FaUpload}
                className="!rounded-xl gap-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsUploadOpen(true);
                }}
              />

              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Lien URL de l’image
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                    <FaImage />
                  </span>
                  <input
                    type="text"
                    name="image1_url"
                    placeholder="https://… ou /uploads/… (rempli automatiquement après upload)"
                    value={formData.image1_url}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Astuce : utilisez <strong>“Téléverser une image”</strong> pour
                  envoyer un visuel (optimisation automatique). Vous pouvez
                  aussi coller une URL ou un chemin local
                  <code className="ml-1">/uploads/…</code>.
                </p>
              </div>
            </div>

            {/* Colonne formulaire */}
            <div className="space-y-4">
              {/* Nom */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Nom *
                </label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              {/* Famille: existante / nouvelle */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Famille (existante)
                  </label>
                  <select
                    name="famille"
                    value={formData.famille}
                    onChange={handleChange}
                    disabled={familyMode === "new"}
                    className={`w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400 ${
                      familyMode === "new"
                        ? "opacity-60 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    <option value="">— Aucune —</option>
                    {families.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Liste triée par ordre alphabétique.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Nouvelle famille
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newFamily}
                      onChange={(e) => setNewFamily(e.target.value)}
                      placeholder="Saisir une nouvelle famille"
                      disabled={familyMode === "existing"}
                      className={`w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400 ${
                        familyMode === "existing"
                          ? "opacity-60 cursor-not-allowed"
                          : ""
                      }`}
                    />
                    <Button
                      type="button"
                      text={familyMode === "existing" ? "Créer" : "Existante"}
                      icon={FaPlus}
                      onClick={() =>
                        setFamilyMode((m) =>
                          m === "existing" ? "new" : "existing"
                        )
                      }
                      className="!rounded-xl gap-2"
                    />
                  </div>
                </div>
              </div>

              {/* Ceinture */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Ceinture (optionnelle)
                </label>
                <select
                  name="ceinture"
                  value={formData.ceinture}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  <option value="">— Aucune —</option>
                  {belts.map((b) => (
                    <option key={`${b.id}-${b.label}`} value={b.label}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Vidéo */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  URL de la vidéo (optionnelle)
                </label>
                <input
                  type="url"
                  name="video_url"
                  placeholder="https://…"
                  value={formData.video_url}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              {/* Traduction */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Traduction (optionnelle)
                </label>
                <input
                  type="text"
                  name="traduction"
                  placeholder="Ex : Nom japonais / anglais…"
                  value={formData.traduction}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              {/* Information */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Informations (optionnelles)
                </label>
                <textarea
                  name="information"
                  rows={4}
                  placeholder="Détails, repères pédagogiques, variantes…"
                  value={formData.information}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              {/* UV2 */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  UV2 (optionnel)
                </label>
                <textarea
                  name="uv2"
                  rows={4}
                  placeholder="Contenu pédagogique / points clés UV2…"
                  value={formData.uv2}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-6">
            <Button
              text="Fermer"
              icon={FaTimes}
              type="button"
              onClick={onClose}
              className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
            />
            <Button
              text={submitting ? "Enregistrement..." : "Enregistrer"}
              icon={FaSave}
              type="submit"
              className={`!rounded-xl gap-2 ${
                submitting ? "opacity-60 pointer-events-none" : ""
              }`}
              disabled={submitting}
            />
          </div>
        </form>
      </div>

      {/* Sous-modale upload image */}
      {isUploadOpen && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4"
          onClick={(e) =>
            e.currentTarget === e.target && setIsUploadOpen(false)
          }
          role="dialog"
          aria-modal="true"
          aria-label="Téléverser une image de technique"
        >
          <div
            className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-800 p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="text-slate-900 dark:text-white font-semibold mb-3">
              Téléverser une image (optimisée)
            </h4>

            <form onSubmit={handleUpload} className="space-y-4">
              <input
                type="file"
                accept="image/*"
                onChange={onFileChange}
                className="block w-full text-sm text-slate-700 dark:text-slate-200"
              />

              <div className="flex items-center gap-2">
                <span className="text-sm">Format</span>
                <select
                  className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-2 py-1 text-sm"
                  value={outputFormat}
                  onChange={(e) => setOutputFormat(e.target.value)}
                  disabled={uploading}
                >
                  <option value="webp">WEBP (recommandé)</option>
                  <option value="jpg">JPG</option>
                  <option value="png">PNG</option>
                </select>
              </div>

              {uploading && (
                <div>
                  <progress
                    className="progress w-full"
                    value={uploadProgress}
                    max="100"
                  />
                  <div className="text-xs mt-1">{uploadProgress}%</div>
                </div>
              )}

              <div className="flex items-center justify-end gap-2">
                <Button
                  type="button"
                  text="Fermer"
                  icon={FaTimes}
                  className="!rounded-xl"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsUploadOpen(false);
                  }}
                />
                <Button
                  type="submit"
                  text={uploading ? "Transfert…" : "Sauvegarder"}
                  icon={FaSave}
                  className="!rounded-xl"
                  disabled={uploading || !selectedFile}
                />
              </div>
            </form>

            <p className="mt-3 text-[11px] text-slate-500 dark:text-slate-400">
              L’image sera redimensionnée côté serveur et convertie au format
              choisi.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
